import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { push as Push } from 'redux-first-history';
import {
    CustomTable,
    TemplatePreview,
    Typography,
    Paper,
    ContentLoader,
    CustomTableRowsProps,
    CustomTableHeadersProps,
    MobileCardRowsProps,
    Pagination,
    MobileTable,
    EmptyState,
} from '@dev/ee-components';
import { isEmpty, map, truncate } from 'lodash-es';
import { CampaignType } from '@dev/ee-api/ee-api-models';
import { safelyEncodeBase64Url } from '@dev/ee-tools';

import { formatNumber } from '@/services/tools/formatNumber';
import { getApiPath } from '@/services/Secuirty';
import { CampaignsPaths } from '@/services/dictionaries/Paths';
import { getAutomationStore, LoadPreviewTemplate } from '@/store/automations/actions';
import { AutomationStepSummaryAndTemplate } from '@/store/automations/types';
import { DEFAULT_PER_PAGE_COUNT } from '@/services/constants';
import { calculateOffset } from '@/services/tools/handleUrlParams';
import useMobile from '@/services/hooks/useMobile';

import { TabNameEnum } from '../../../Campaigns/CampaignReport/CampaignReportInterface';

const Container = styled.div`
    margin-top: 32px;
`;

const MobileTableContainer = styled.div`
    margin-top: 24px;

    & > div {
        padding-bottom: 2px;
    }
`;

const columnWidth: Record<string, string> = {
    preview: '10%',
    template: '30%',
    delivered: '10%',
    opened: '10%',
    clicked: '9%',
    bounced: '10%',
    unsubscribed: '10%',
    viewreport: '130px',
};

const AutomationsTableWithPagination: FC = (): JSX.Element => {
    const automations = useSelector(getAutomationStore);
    const { t } = useTranslation();

    const initialEmailListState: {
        emailsListRecords: AutomationStepSummaryAndTemplate[];
        page: number;
        perPage: number;
    } = {
        emailsListRecords: [],
        page: 1,
        perPage: DEFAULT_PER_PAGE_COUNT,
    };
    const [emailListState, setEmailsListState] = useState(initialEmailListState);

    useEffect(() => {
        setEmailsListState({ ...emailListState, emailsListRecords: automations?.emailsList });
    }, [automations?.emailsList]);

    const headers = useTableHeaders();
    const rows = useTableRows(emailListState.page, emailListState.perPage);

    const changePage = (page, perPage) => {
        setEmailsListState({ ...emailListState, page, perPage });
    };

    return (
        <Container>
            <Paper padding={{ top: 24 }}>
                <CustomTable
                    headers={headers}
                    rows={rows}
                    isEmpty={isEmpty(automations?.emailsList) && !automations?.loadingSingleAutomation}
                    loading={automations?.loadingSingleAutomation}
                    rowPadding={48}
                    rowHeight={112}
                    hideCheckboxes={true}
                    columnsWidth={columnWidth}
                    emptyStateProps={{ text: t('thereisnodatatodisplay') }}
                    disableFirstRowBorderTop
                />
                {automations?.emailsList?.length > DEFAULT_PER_PAGE_COUNT && (
                    <Pagination
                        perPage={emailListState?.perPage}
                        activePage={emailListState?.page}
                        dataLength={automations?.emailsList?.length}
                        handlePaginationChange={changePage}
                    />
                )}
            </Paper>
        </Container>
    );
};

const MobileTableWithPaginaion: FC = (): JSX.Element => {
    const automations = useSelector(getAutomationStore);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const initialEmailListState: {
        emailsListRecords: AutomationStepSummaryAndTemplate[];
        page: number;
        perPage: number;
    } = {
        emailsListRecords: [],
        page: 1,
        perPage: DEFAULT_PER_PAGE_COUNT,
    };
    const [emailListState, setEmailsListState] = useState(initialEmailListState);

    useEffect(() => {
        setEmailsListState({ ...emailListState, emailsListRecords: automations?.emailsList });
    }, [automations?.emailsList]);

    const header = useMobileTableHeaders();
    const rows = useMobileTableRows(emailListState.page, emailListState.perPage);

    const changePage = (page, perPage) => {
        setEmailsListState({ ...emailListState, page, perPage });
    };

    if (isEmpty(automations?.emailsList) && !automations?.loadingSingleAutomation) {
        return <EmptyState text={t('thereisnodatatodisplay')} />;
    }

    return (
        <>
            <MobileTableContainer>
                <MobileTable
                    headers={header}
                    rows={rows}
                    buttonText={t('viewreport')}
                    onClickButton={(ev, rows): void => {
                        ev.stopPropagation();
                        dispatch(
                            Push(
                                CampaignsPaths.CampaignReport.replace(':type', CampaignType.SendNow)
                                    .replace(':name', safelyEncodeBase64Url(rows.returnOnItemClick))
                                    .replace(':tab', TabNameEnum.Overview)
                            )
                        );
                    }}
                    disableSimplePopover={true}
                />
            </MobileTableContainer>
            {automations?.emailsList?.length > DEFAULT_PER_PAGE_COUNT && (
                <Paper padding={{ top: 24 }}>
                    <Pagination
                        perPage={emailListState?.perPage}
                        activePage={emailListState?.page}
                        dataLength={automations?.emailsList?.length}
                        handlePaginationChange={changePage}
                    />
                </Paper>
            )}
        </>
    );
};

const useTableHeaders = (): CustomTableHeadersProps[] => {
    const { t } = useTranslation();

    return [
        {
            id: 'preview',
            numeric: false,
            label: '',
        },
        { id: 'template', headerAlign: 'left', columnAlign: 'left', label: t('template') },
        {
            id: 'delivered',
            headerAlign: 'right',
            label: t('delivered'),
            columnAlign: 'right',
        },
        {
            id: 'opened',
            headerAlign: 'right',
            label: t('opened'),
            columnAlign: 'right',
        },
        {
            id: 'clicked',
            headerAlign: 'right',
            label: t('clicked'),
            columnAlign: 'right',
        },
        {
            id: 'bounced',
            headerAlign: 'right',
            label: t('bounced'),
            columnAlign: 'right',
        },
        {
            id: 'unsubscribed',
            headerAlign: 'right',
            label: t('unsubscribed'),
            columnAlign: 'right',
        },
        {
            id: 'viewreport',
            headerAlign: 'center',
            label: '',
            columnAlign: 'center',
        },
    ];
};

const tableCounts = (count, percentage): JSX.Element => (
    <>
        <Typography variant="caption">{formatNumber(count)}</Typography>

        <Typography variant="body1" bodyVariant={'help'}>
            {`${percentage}%`}
        </Typography>
    </>
);

const useTableRows = (page: number, perPage: number): CustomTableRowsProps[] => {
    const automations = useSelector(getAutomationStore);
    const dispatch = useDispatch();
    const offset = calculateOffset(page, perPage);
    const limit = perPage * page;
    const { t } = useTranslation();

    if (automations?.loadingSingleAutomation) {
        const rows: CustomTableRowsProps[] = [];

        for (let index = 0; index < perPage; index++) {
            rows?.push({
                preview: <ContentLoader mode="row" height={70} width={70} />,
                template: <ContentLoader mode="row" height={20} />,
                delivered: <ContentLoader mode="row" height={20} />,
                opened: <ContentLoader mode="row" height={20} />,
                clicked: <ContentLoader mode="row" height={20} />,
                bounced: <ContentLoader mode="row" height={20} />,
                unsubscribed: <ContentLoader mode="row" height={20} />,
                viewreports: <ContentLoader mode="row" height={20} />,
            });
        }

        return rows;
    }

    const emailList = map(automations?.emailsList, (email: AutomationStepSummaryAndTemplate): CustomTableRowsProps => {
        const emailTemplatesThumbnail = email?.TemplateData?.MediumScreenshot
            ? `${getApiPath()}/userfile${email.TemplateData.MediumScreenshot}`
            : '';
        return {
            preview: (
                <div
                    onClick={(ev): void => {
                        ev.stopPropagation();
                    }}
                >
                    {
                        <TemplatePreview
                            split={false}
                            screenshot={emailTemplatesThumbnail}
                            onClick={(): void => {
                                email?.IsTemplateDeleted
                                    ? null
                                    : dispatch(LoadPreviewTemplate(email?.TemplateData?.Name));
                            }}
                            disabled={email?.IsTemplateDeleted}
                        />
                    }
                </div>
            ),
            template: (
                <div style={{ wordBreak: 'break-all', paddingRight: '10px' }}>
                    <Typography variant="caption">
                        {email?.IsTemplateDeleted ? t('templatedeleted') : truncate(email.Channel, { length: 70 })}
                    </Typography>
                </div>
            ),
            delivered: tableCounts(email.DeliveredCount, email.AverageDeliverRate),
            opened: tableCounts(email.OpenedCount, email.AverageOpenRate),
            clicked: tableCounts(email.ClickedCount, email.AverageClickRate),
            bounced: tableCounts(email.BouncedCount, email.AverageBounceRate),
            unsubscribed: tableCounts(email.UnsubscribedCount, email.AverageUnsubscribeRate),
            viewreport: <ViewReports name={email.Channel} />,
        };
    });

    return emailList.slice(offset, limit);
};

const useMobileTableHeaders = (): string[] => {
    const { t } = useTranslation();
    return [t('delivered'), t('opened'), t('clicked'), t('bounced'), t('unsubscribed')];
};
const useMobileTableRows = (page: number, perPage: number): MobileCardRowsProps[] => {
    const automations = useSelector(getAutomationStore);
    const { t } = useTranslation();
    const offset = calculateOffset(page, perPage);
    const limit = perPage * page;

    const SkeletonTemplate = {
        tableTitle: <ContentLoader mode="row" />,
        delivered: <ContentLoader mode="row" />,
        opened: <ContentLoader mode="row" />,
        clicked: <ContentLoader mode="row" />,
        bounced: <ContentLoader mode="row" />,
        unsubscribed: <ContentLoader mode="row" />,
    };

    const SkeletonRows = map([...new Array(perPage)], (): MobileCardRowsProps => SkeletonTemplate);
    const rows = map(
        automations?.emailsList,
        (email: AutomationStepSummaryAndTemplate): MobileCardRowsProps => ({
            tableTitle: email?.IsTemplateDeleted ? t('templatedeleted') : truncate(email.Channel, { length: 70 }),
            delivered: tableCounts(email.DeliveredCount, email.AverageDeliverRate),
            opened: tableCounts(email.OpenedCount, email.AverageOpenRate),
            clicked: tableCounts(email.ClickedCount, email.AverageClickRate),
            bounced: tableCounts(email.BouncedCount, email.AverageBounceRate),
            unsubscribed: tableCounts(email.UnsubscribedCount, email.AverageUnsubscribeRate),
            returnOnItemClick: email.Channel,
        })
    );

    return automations?.loadingSingleAutomation ? SkeletonRows : rows.slice(offset, limit);
};

const ViewReports = (props: { name }): JSX.Element => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    return (
        <div
            onClick={(ev): void => {
                ev.stopPropagation();
                dispatch(
                    Push(
                        CampaignsPaths.CampaignReport.replace(':type', CampaignType.SendNow)
                            .replace(':name', safelyEncodeBase64Url(props.name))
                            .replace(':tab', TabNameEnum.Overview)
                    )
                );
            }}
        >
            <Typography variant="body1" bodyVariant="link">
                {t('viewreports')}
            </Typography>
        </div>
    );
};

export const WorkflowEmails = (): JSX.Element => {
    const { t } = useTranslation();
    const isMobile = useMobile();

    return (
        <>
            <Typography variant="h1">{t('workflowemails')}</Typography>

            {isMobile ? <MobileTableWithPaginaion /> : <AutomationsTableWithPagination />}
        </>
    );
};
